import { useCallback, useEffect, useRef, useState } from 'react';
import { ReactFlowProvider, useReactFlow } from '@xyflow/react';
import { toPng } from 'html-to-image';
import { DetailsPanel } from './components/DetailsPanel';
import { PfdCanvas, type FocusRequest } from './components/PfdCanvas';
import { SourcesModal } from './components/SourcesModal';
import { StatusStrip } from './components/StatusStrip';
import { Toolbar } from './components/Toolbar';
import { usePlaybackController, useReducedMotion } from './hooks';
import { useDiagramStore } from './state/store';

function AppShell() {
  const rootRef = useRef<HTMLDivElement>(null);
  const flow = useReactFlow();
  const state = useDiagramStore();
  const reducedMotion = useReducedMotion();
  const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);
  const [fullscreen, setFullscreen] = useState(Boolean(document.fullscreenElement));
  usePlaybackController();

  const focusNode = useCallback((id: string) => {
    state.selectNode(id);
    setFocusRequest({ id, key: Date.now() });
  }, [state]);

  const fitView = useCallback(() => {
    void flow.fitView({ padding: 0.055, minZoom: 0.2, maxZoom: 0.78, duration: reducedMotion ? 0 : 500 });
  }, [flow, reducedMotion]);

  const toggleFullscreen = useCallback(async () => {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await rootRef.current?.requestFullscreen();
  }, []);

  const exportPng = useCallback(async () => {
    const element = rootRef.current?.querySelector('.canvas-shell') as HTMLElement | null;
    if (!element) return;
    const url = await toPng(element, { cacheBust: true, pixelRatio: 1.5, backgroundColor: '#f3f1eb' });
    const link = document.createElement('a');
    link.download = `sinteks-customer-data-pfd-${new Date().toISOString().slice(0, 10)}.png`;
    link.href = url;
    link.click();
  }, []);

  useEffect(() => {
    document.documentElement.lang = state.language;
    const onFullscreen = () => setFullscreen(Boolean(document.fullscreenElement));
    const focusSelected = () => { if (state.selectedNodeId) setFocusRequest({ id: state.selectedNodeId, key: Date.now() }); };
    document.addEventListener('fullscreenchange', onFullscreen);
    window.addEventListener('pfd:focus-selected', focusSelected);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreen);
      window.removeEventListener('pfd:focus-selected', focusSelected);
    };
  }, [state]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable;
      if (typing && event.key !== 'Escape') return;
      if (event.key === ' ') {
        event.preventDefault();
        if (state.status === 'playing') state.pause();
        else state.play();
      }
      else if (event.key.toLowerCase() === 'r') state.restart();
      else if (event.key === 'ArrowLeft') state.previous();
      else if (event.key === 'ArrowRight') state.next();
      else if (event.key.toLowerCase() === 'f') void toggleFullscreen();
      else if (event.key === '/') { event.preventDefault(); window.dispatchEvent(new Event('pfd:focus-search')); }
      else if (event.key === 'Escape') {
        if (state.sourcesOpen) state.setSourcesOpen(false);
        else if (state.searchOpen) state.setSearchOpen(false);
        else if (state.selectedNodeId) state.selectNode(null);
        else if (document.fullscreenElement) void document.exitFullscreen();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, toggleFullscreen]);

  return (
    <div className={`app-shell ${fullscreen ? 'is-fullscreen' : ''}`} ref={rootRef}>
      <Toolbar onFitView={fitView} onFullscreen={toggleFullscreen} onExport={exportPng} onSearchSelect={focusNode} fullscreen={fullscreen} />
      <main className="diagram-workspace">
        <PfdCanvas reducedMotion={reducedMotion} focusRequest={focusRequest} />
        <DetailsPanel />
      </main>
      <StatusStrip reducedMotion={reducedMotion} />
      <SourcesModal />
    </div>
  );
}

export function App() {
  return <ReactFlowProvider><AppShell /></ReactFlowProvider>;
}
